import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsApi, stockApi } from '../api/client';
import QRScanner from './QRScanner';
import Card from './Card';
import Button from './Button';
import Input from './Input';
import { parseQrCode } from '../utils/qrParser';

const AddProductModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('item_code'); // 'scan' or 'item_code' - по умолчанию 'item_code'
  const [itemCode, setItemCode] = useState('');
  const [error, setError] = useState(null);
  const [foundProduct, setFoundProduct] = useState(null);
  const isProcessingRef = useRef(false);
  const lastProcessedQrRef = useRef(null);

  if (!isOpen) return null;

  const handleQrScan = async (qr) => {
    if (isProcessingRef.current) {
      return;
    }
    
    if (lastProcessedQrRef.current === qr) {
      return;
    }
    
    isProcessingRef.current = true;
    lastProcessedQrRef.current = qr;
    
    try {
      const qrData = parseQrCode(qr);
      const productCode = qrData?.product_code;
      
      if (!productCode) {
        throw new Error('Не удалось извлечь код товара из QR-кода');
      }
      
      const response = await productsApi.getByPc(productCode);
      const product = response.data;
      
      try {
        const stockResponse = await stockApi.getStock(product.id);
        product.stock = stockResponse.data?.total_stock ?? stockResponse.data?.stock ?? 0;
      } catch (stockError) {
        product.stock = 0;
      }
      
      setFoundProduct(product);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Товар не найден');
        setFoundProduct(null);
      } else {
        setError('Ошибка сканирования QR-кода');
      }
    } finally {
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 1000);
    }
  };

  const handleSearchByItemCode = async () => {
    if (!itemCode.trim()) {
      setError('Введите артикул товара');
      return;
    }

    if (isProcessingRef.current) {
      return;
    }

    isProcessingRef.current = true;
    setError(null);

    try {
      const response = await productsApi.getByItemCode(itemCode.toUpperCase());
      const data = response.data;
      
      // Если это массив товаров (несколько товаров с одинаковым артикулом, но разными диоптриями)
      if (Array.isArray(data)) {
        if (data.length === 1) {
          setFoundProduct(data[0]);
        } else {
          // Если несколько товаров с разными диоптриями, показываем первый
          setFoundProduct(data[0]);
        }
      } else {
        // Если один товар
        setFoundProduct(data);
      }
      
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Товар не найден');
        setFoundProduct(null);
      } else {
        setError('Ошибка поиска товара');
      }
    } finally {
      isProcessingRef.current = false;
    }
  };

  const handleCreateProduct = () => {
    const params = new URLSearchParams();
    if (itemCode.trim()) {
      params.set('item_code', itemCode.toUpperCase());
    }
    navigate(`/products/new?${params.toString()}`);
    onClose();
  };

  const handleViewProduct = () => {
    if (foundProduct) {
      navigate(`/products/${foundProduct.id}`);
      onClose();
    }
  };

  const handleReset = () => {
    setMode(null);
    setItemCode('');
    setError(null);
    setFoundProduct(null);
    isProcessingRef.current = false;
    lastProcessedQrRef.current = null;
  };

  // Если товар найден, показываем его информацию
  if (foundProduct) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Товар найден</h3>
            <div className="mb-3">
              <h4 className="text-base font-semibold text-gray-900 mb-1">
                {foundProduct.name}
              </h4>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {(foundProduct.diameter !== null && foundProduct.diameter !== undefined) && (
                  <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                    Диаметр: {parseFloat(foundProduct.diameter).toFixed(1)}
                  </span>
                )}
              </div>
              <p className={`text-sm font-semibold ${
                (foundProduct.stock || 0) > 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                Остаток: {foundProduct.stock || 0}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              size="md"
              className="flex-1"
              onClick={handleReset}
            >
              Назад
            </Button>
            <Button
              variant="primary"
              size="md"
              className="flex-1"
              onClick={handleViewProduct}
            >
              Просмотр
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Если выбран режим сканирования
  if (mode === 'scan') {
    return (
      <QRScanner
        onScan={handleQrScan}
        onError={(err) => {
          setError(`Ошибка сканирования: ${err}`);
        }}
        onClose={() => {
          handleReset();
          onClose();
        }}
      />
    );
  }

  // Основное меню с полем ввода артикула
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Добавить товар</h3>
          
          {mode === 'item_code' ? (
            <div>
              <Input
                label="Артикул"
                value={itemCode}
                onChange={(e) => setItemCode(e.target.value.toUpperCase())}
                placeholder="Введите артикул товара"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchByItemCode();
                  }
                }}
                autoFocus
              />
              {error && (
                <div className="mb-3 p-2 bg-red-50 text-red-600 text-sm rounded">
                  {error}
                </div>
              )}
              {error === 'Товар не найден' && (
                <div className="mb-3">
                  <Button
                    variant="success"
                    size="md"
                    className="w-full"
                    onClick={handleCreateProduct}
                  >
                    Создать товар с артикулом {itemCode.toUpperCase()}
                  </Button>
                </div>
              )}
              <div className="flex gap-3 mb-3">
                <Button
                  variant="primary"
                  size="md"
                  className="flex-1"
                  onClick={handleSearchByItemCode}
                  disabled={!itemCode.trim()}
                >
                  Найти
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  className="flex-1"
                  onClick={() => setMode('scan')}
                >
                  📷 QR-код
                </Button>
              </div>
              <Button
                variant="secondary"
                size="md"
                className="w-full"
                onClick={onClose}
              >
                Отмена
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={() => setMode('scan')}
              >
                📷 Сканировать QR-код
              </Button>
              <Button
                variant="secondary"
                size="lg"
                className="w-full"
                onClick={() => setMode('item_code')}
              >
                🔍 Ввести артикул
              </Button>
              <Button
                variant="secondary"
                size="md"
                className="w-full"
                onClick={onClose}
              >
                Отмена
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default AddProductModal;

